import React from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle,
  ArrowRight,
  BarChart2,
  Code,
  Shield,
  PenTool,
  Zap,
  HelpCircle,
  ExternalLink
} from "lucide-react";
import SEO from "../components/SEO";
import "./Home.css";

const Home = () => {

  const faqData = [
    {
      question: "What core services does Vektrixo Digital provide?",
      answer: "We specialize in automated and manual software testing (QA), high-performance static web development (React and Next.js), agile product management, and strategic business consulting. Our Pune-based engineering team helps businesses deliver reliable, fast-loading digital products."
    },
    {
      question: "How do you ensure software quality in QA testing projects?",
      answer: "We build robust automated test suites using frameworks like Playwright and Selenium, alongside detailed manual testing. Our regression, integration, and API testing processes identify bottlenecks early in the development lifecycle to ensure bug-free production launches."
    },
    {
      question: "What technologies do you use for web development?",
      answer: "We focus on modern, performance-first frameworks like React, Next.js, and static site architectures. By separating frontend design from backend complexity, we deliver secure, fast, and SEO-friendly sites that achieve perfect PageSpeed scores."
    },
    {
      question: "How does Vektrixo Digital collaborate with startups and scaling enterprises?",
      answer: "We offer end-to-end support from discovery to launch. We help map product roadmaps, prioritize MVP features, automate workflows, and set up marketing consulting funnels to drive real conversion and growth."
    }
  ];

  return (
    <main className="home-container">
      <SEO
        title="Vektrixo Digital | Best Software Testing QA & Web Development Agency in India"
        description="Vektrixo Digital is the leading Software Testing (QA) Automation and Web Development agency in India. We engineer lightning-fast React/Next.js sites and bulletproof QA automation suites."
        keywords="Best Software Testing Pune, QA Automation Agency Pune, Static Web Development Pune, Custom Web Development, React Developer Pune, Next.js Developer Pune, Business Consulting India, Vektrixo Digital, Software QA Agency Pune, Software Testing Agency Pune"
        canonical="https://www.vektrixo.com/"
      />
      {/* 1. HERO SECTION */}
      <section className="hero">
        <div className="hero-content">
          <span className="badge">New Era of Digital Solutions</span>
          <h1>
            Empowering Brands with{" "}
            <span className="highlight">Precision & Design</span>
          </h1>
          <p>
            We help startups and businesses design, develop, and optimize
            digital experiences that load faster, rank higher, and convert.
          </p>
          <div className="hero-btns">
            <Link to="/contact" className="btn-primary">
              Start Your Journey <ArrowRight size={18} />
            </Link>
            <Link to="/services" className="btn-outline">
              Explore Expertise
            </Link>
          </div>
        </div>
      </section>

      {/* --- AUDITOR PRO SECTION (The Bridge) --- */}
      {/* --- AUDITOR PRO SECTION (Simplified CTA) --- */}
      <section className="audit-tool-section">
        <div className="audit-card-compact">
          <div className="audit-visual">
            {/* Ek cool scanning effect ya icon */}
            <div className="pulse-icon">
              <Zap size={40} color="#38bdf8" />
            </div>
          </div>

          <div className="audit-text">
            <div className="tool-tag">Vektrixo AuditorPro v2.0</div>
            <h2>Free Website <span className="highlight">Technical Audit</span></h2>
            <p>Identify SEO gaps, performance bottlenecks, and security vulnerabilities in seconds.</p>
          </div>

          <div className="audit-action">
            <a
              href="https://auditorpro.vektrixo.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="run-audit-btn-large"
            >
              OPEN AUDITOR PRO <ArrowRight size={20} />
            </a>
            <span className="trust-text">Simple Signup Required • Professional Insights</span>
          </div>
        </div>
      </section>

      {/* 2. STATS SECTION */}
      <section className="stats-strip">
        <div className="stat-item">
          <h3>10+</h3>
          <p>Global Clients</p>
        </div>
        <div className="stat-item">
          <h3>15+</h3>
          <p>Projects Delivered</p>
        </div>
        <div className="stat-item">
          <h3>200+</h3>
          <p>Bugs Identified</p>
        </div>
        <div className="stat-item">
          <h3>99%</h3>
          <p>Success Rate</p>
        </div>
      </section>

      {/* 3. BENTO SERVICES PREVIEW */}
      <section className="home-services">
        <h2 className="section-title">
          What We Do <span className="highlight">Best</span>
        </h2>
        <div className="bento-grid">
          {/* Card 1: Web Development (Featured, Wide) */}
          <div className="bento-card wide featured">
            <div className="bento-content-split">
              <div className="bento-main-info">
                <div>
                  <div className="bento-icon-wrapper">
                    <Code color="#38bdf8" size={28} />
                  </div>
                  <h3>Web Development</h3>
                  <p>High-performance websites engineered for speed, search engines, and maximum conversion.</p>
                </div>
                <Link to="/services" className="bento-link">
                  Explore Services &rarr;
                </Link>
              </div>
              <ul className="bento-bullet-list">
                <li>Custom Landing Pages</li>
                <li>Speed & SEO Optimization</li>
                <li>Responsive Frontend Code</li>
              </ul>
            </div>
          </div>

          {/* Card 2: UI/UX & Design (Normal) */}
          <div className="bento-card">
            <div>
              <div className="bento-icon-wrapper">
                <PenTool color="#38bdf8" size={24} />
              </div>
              <h3>UI/UX & Design</h3>
              <p>Modern layouts and clean visual designs that engage your audience.</p>
            </div>
            <Link to="/services" className="bento-link-small">
              Learn More &rarr;
            </Link>
          </div>

          {/* Card 3: QA & Testing (Normal) */}
          <div className="bento-card">
            <div>
              <div className="bento-icon-wrapper">
                <Shield color="#38bdf8" size={24} />
              </div>
              <h3>QA & Testing</h3>
              <p>Thorough manual and automated testing to prevent crashes and bugs.</p>
            </div>
            <Link to="/services" className="bento-link-small">
              Learn More &rarr;
            </Link>
          </div>

          {/* Card 4: Strategy & Growth (Wide) */}
          <div className="bento-card wide">
            <div className="bento-content-split">
              <div className="bento-main-info">
                <div>
                  <div className="bento-icon-wrapper">
                    <BarChart2 color="#38bdf8" size={24} />
                  </div>
                  <h3>Strategy & Consulting</h3>
                  <p>Actionable technology consulting to align your roadmap with business growth.</p>
                </div>
                <Link to="/services" className="bento-link">
                  Explore Strategy &rarr;
                </Link>
              </div>
              <ul className="bento-bullet-list">
                <li>Product Roadmapping</li>
                <li>Scale & Speed Advisory</li>
                <li>Process Automation</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 4. WHY PARTNER WITH US */}
      <section className="why-us">
        <div className="why-header">
          <h2>Why Partner with <span className="highlight">Vektrixo?</span></h2>
          <p>We work as your extended engineering and strategy team to launch high-performance digital products.</p>
        </div>
        <div className="why-grid">
          <div className="why-card">
            <CheckCircle size={24} className="icon" />
            <h3>Performance-First</h3>
            <p>We build lightweight, ultra-fast websites designed to keep users engaged and convert them into customers.</p>
          </div>
          <div className="why-card">
            <CheckCircle size={24} className="icon" />
            <h3>End-to-End Build</h3>
            <p>From custom modern design prototypes to clean frontend engineering and final deployment.</p>
          </div>
          <div className="why-card">
            <CheckCircle size={24} className="icon" />
            <h3>Results-Driven</h3>
            <p>Every layout, button, and user flow is built with clear business targets and conversions in mind.</p>
          </div>
          <div className="why-card">
            <CheckCircle size={24} className="icon" />
            <h3>Consultative Approach</h3>
            <p>We provide technical consulting to align your software stack and roadmap with your growth goals.</p>
          </div>
        </div>
      </section>


      {/* 5. VEKTRIXO LABS (Backlinks to network sites for SEO equity) */}
      <section className="labs-section">
        <div className="labs-container">
          <div className="labs-info">
            <span className="badge">Our Ecosystem</span>
            <h2>Proprietary <span className="highlight">Platforms & Products</span></h2>
            <p>
              We don't just build client products; we design and run some of the fastest-growing automated digital platforms in the market.
            </p>
          </div>
          <div className="labs-grid">
            <div className="labs-card">
              <div className="labs-card-header">
                <span className="labs-card-tag">Technical SEO Tool</span>
                <a href="https://auditorpro.vektrixo.com" target="_blank" rel="noopener noreferrer" className="labs-link">
                  Run Free Scan <ExternalLink size={14} />
                </a>
              </div>
              <h3>Vektrixo AuditorPro</h3>
              <p>
                Identify SEO gaps, security vulnerabilities, and page speed performance bottlenecks in seconds. Built for rapid technical audits.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. FINAL CTA SECTION */}
      <section className="final-cta">
        <h2>Ready to Elevate Your <span className="highlight">Digital Presence?</span></h2>
        <p>
          Whether you want a high-performance web development build, robust QA testing pipelines, or a strategic business consult, our team is ready to deliver.
        </p>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Link to="/contact" className="btn-primary">
            Book Your Free Consultation <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* 6. FAQ (AEO/GEO & User Optimization) */}
      <section className="geo-faq-section">
        <h2 className="section-title">
          Frequently Asked <span className="highlight">Questions</span>
        </h2>
        <p className="section-subtitle">
          Find answers to common questions about Vektrixo Digital's software testing, QA automation, and web development services.
        </p>

        <div className="faq-grid">
          {faqData.map((faq, index) => (
            <div key={index} className="faq-card">
              <div className="faq-question">
                <HelpCircle size={20} className="faq-icon" />
                <h3>{faq.question}</h3>
              </div>
              <div className="faq-answer">
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default Home;
